import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Badge from "aldoo-ra/Badge"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import Input from "aldoo-ra/Input"
import moment from "moment"
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import { renderPlatformIcon } from "../utils/platformIconUtils.jsx"
import APIKeyForm from "./components/APIKeyForm.jsx"
import { ShieldCheckIcon, KeyIcon, DevicePhoneMobileIcon, TrashIcon, CameraIcon, PencilIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { updateUserAvatarOnPage } from "./avatar-update-helper"

/**
 * DevicesTab - Device information display for user
 */
const DevicesTab = ({ user }) => {
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const crudAPI = api("crud")

  useEffect(() => {
    const loadDevices = async () => {
      if (!user || !user.devices || !Array.isArray(user.devices)) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Extract device IDs - handle both string IDs and objects
        const deviceIds = user.devices.map(d => {
          if (typeof d === 'string') return d
          if (d && d._id) return d._id
          if (d && d.$oid) return d.$oid
          return null
        }).filter(Boolean)

        console.log("Loading devices for user:", { userId: user._id, deviceIds, rawDevices: user.devices })

        if (deviceIds.length === 0) {
          setDevices([])
          setLoading(false)
          return
        }

        // Fetch full device information
        const response = await crudAPI({
          get: {
            model: "UserDevice",
            match: { _id: { $in: deviceIds } }
          }
        })

        console.log("Device fetch response:", response)

        // Handle different response formats
        if (Array.isArray(response)) {
          setDevices(response)
        } else if (response.result !== false && response.data) {
          setDevices(response.data)
        } else {
          setDevices([])
        }
      } catch (error) {
        console.error("Failed to load devices:", error)
        setDevices([])
      } finally {
        setLoading(false)
      }
    }

    loadDevices()
  }, [user])

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Model"

    if (!userAgent.includes("Mozilla")) {
      return userAgent
    }

    try {
      let browser = "Unknown Browser"
      let os = "Unknown OS"

      if (userAgent.includes("Chrome")) {
        browser = "Chrome"
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
      } else if (userAgent.includes("Edge")) {
        browser = "Edge"
      } else if (userAgent.includes("Mozilla")) {
        browser = "Mozilla"
      }

      if (userAgent.includes("Macintosh") || userAgent.includes("Mac OS X")) {
        os = "macOS"
      } else if (userAgent.includes("Windows")) {
        os = "Windows"
      } else if (userAgent.includes("Linux")) {
        os = "Linux"
      } else if (userAgent.includes("Android")) {
        os = "Android"
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
        os = "iOS"
      }

      return `${browser} • ${os}`
    } catch (error) {
      return userAgent
    }
  }

  const getStatusBadge = (device) => {
    if (!device.enabled) return <Badge variant="admin_error">Disabled</Badge>
    if (device.online) return <Badge variant="admin_success">Online</Badge>
    if (device.login) return <Badge variant="admin_info">Logged In</Badge>
    return <Badge variant="admin_warning">Offline</Badge>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Typography variant="admin_txt1_dim">Loading devices...</Typography>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <Typography variant="admin_label" className="my-0">
          Connected Devices ({devices.length})
        </Typography>
        {user.deviceLimit && user.deviceLimit > 0 && devices.length > user.deviceLimit && (
          <Badge variant="admin_error">Over Limit</Badge>
        )}
      </div>
      {devices.length > 0 ? (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          {devices.map((device, idx) => (
            <div key={idx} className="bg-admin_canvas rounded-lg px-4 py-3 border border-admin_bordered overflow-hidden">
              <div className="flex items-start gap-3 min-w-0">
                <Tooltip content={device.platform || "Unknown"}>
                  {renderPlatformIcon(device.platform, "w-6 h-6 text-admin_text_secondary flex-shrink-0 mt-0.5")}
                </Tooltip>

                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        !device.enabled ? 'bg-admin_error' :
                        device.online ? 'bg-admin_success' :
                        device.login ? 'bg-admin_info' :
                        'bg-admin_warning'
                      }`}
                      title={
                        !device.enabled ? 'Disabled' :
                        device.online ? 'Online' :
                        device.login ? 'Logged In' :
                        'Offline'
                      }
                    />
                    <Typography variant="admin_body" className="my-0 font-medium truncate">
                      {device.name || device.deviceName || "Unknown Device"}
                    </Typography>
                  </div>

                  <div className="space-y-0.5">
                    {device.ip && (
                      <Tooltip content={
                        <div className="text-center">
                          <Typography variant="admin_txt1" className="block">{device.ip}</Typography>
                          <Typography variant="admin_txt2_dim">Click to copy</Typography>
                        </div>
                      }>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(device.ip)
                              showNotification({
                                message: "IP copied to clipboard",
                                className: "bg-admin_primary text-white"
                              })
                            } catch (error) {
                              console.error("Failed to copy:", error)
                            }
                          }}
                          className="text-left truncate block w-full hover:text-admin_primary transition-colors"
                        >
                          <Typography variant="admin_txt2" className="my-0 text-admin_text_secondary truncate block">
                            <span className="font-bold">IP:</span> <span className="font-mono">{device.ip}</span>
                          </Typography>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 text-right flex flex-col gap-0.5">
                  {device.lastLogin && (
                    <Tooltip content={moment(device.lastLogin).format("MMM DD, YYYY HH:mm")}>
                      <Typography variant="admin_txt2" className="my-0 text-admin_text_secondary whitespace-nowrap">
                        <span className="font-bold">Last Login:</span> {moment(device.lastLogin).fromNow()}
                      </Typography>
                    </Tooltip>
                  )}
                  {device.deviceID && (
                    <Tooltip content={
                      <div className="text-center">
                        <Typography variant="admin_txt1" className="block">{device.deviceID}</Typography>
                        <Typography variant="admin_txt2_dim">Click to copy</Typography>
                      </div>
                    }>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(device.deviceID)
                            showNotification({
                              message: "Device ID copied to clipboard",
                              className: "bg-admin_primary text-white"
                            })
                          } catch (error) {
                            console.error("Failed to copy:", error)
                          }
                        }}
                        className="px-2 py-1 border border-admin_bordered rounded hover:border-admin_primary hover:text-admin_primary transition-colors"
                      >
                        <Typography variant="admin_txt2" className="my-0 text-admin_text_secondary whitespace-nowrap">
                          <span className="font-bold">Device ID</span>
                        </Typography>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Typography variant="admin_txt2_dim">No devices connected</Typography>
      )}
    </div>
  )
}

/**
 * UserInfoDrawer - User information display with actions
 *
 * Displays comprehensive user information in a drawer layout.
 * Can be opened with a user ID or user object.
 *
 * @param {boolean} isOpen - Whether the drawer is open
 * @param {function} onClose - Callback when drawer closes
 * @param {string} userId - User MongoDB ObjectId (_id field)
 * @param {Object} userObject - User object (if already loaded)
 * @param {function} onDelete - Optional callback when user is deleted
 * @param {function} onStatusChange - Optional callback when user status changes
 */
export default function UserInfoDrawer({ isOpen, onClose, userId, userObject, onDelete, onStatusChange }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("details")
  const [apiKeys, setApiKeys] = useState([])
  const [loadingApiKeys, setLoadingApiKeys] = useState(false)
  const [showInspectModal, setShowInspectModal] = useState(false)
  const [selectedApiKey, setSelectedApiKey] = useState(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [updatingAvatar, setUpdatingAvatar] = useState(false)
  const [permissions, setPermissions] = useState([])
  const [loadingPermissions, setLoadingPermissions] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [editedName, setEditedName] = useState("")
  const [storeData, setStoreData] = useState(null)
  const [loadingStore, setLoadingStore] = useState(false)

  useEffect(() => {
    const loadUser = async () => {
      if (!isOpen) {
        return
      }

      // If user object is provided directly, use it
      if (userObject) {
        setUser(userObject)
        setLoading(false)
        setError(null)
        return
      }

      // Otherwise load from API
      if (!userId) {
        setError("No user ID provided")
        return
      }

      setLoading(true)
      setError(null)

      try {
        const response = await UserPlatformAPI.GetUserDetails(userId)

        if (response && response.result) {
          setUser(response.data)
        } else {
          setError(response.error || "User not found")
        }
      } catch (err) {
        console.error("Failed to load user:", err)
        setError("Failed to load user information")
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [isOpen, userId, userObject])

  // Load API keys for the user
  useEffect(() => {
    const loadApiKeys = async () => {
      if (!isOpen || !user || !user._id) {
        return
      }

      setLoadingApiKeys(true)
      try {
        const crudAPI = api("crud")
        const response = await crudAPI({
          get: {
            model: "APIKey",
            match: { user: user._id },
            select: "key description status validUntil lastUsedAt createdAt",
            sort: { createdAt: -1 }
          }
        })

        if (Array.isArray(response)) {
          setApiKeys(response)
        } else if (response.data && Array.isArray(response.data)) {
          setApiKeys(response.data)
        } else {
          setApiKeys([])
        }
      } catch (error) {
        console.error("Failed to load API keys:", error)
        setApiKeys([])
      } finally {
        setLoadingApiKeys(false)
      }
    }

    loadApiKeys()
  }, [isOpen, user])

  // Load permissions for the user
  useEffect(() => {
    const loadPermissions = async () => {
      if (!isOpen || !user || !user._id) {
        return
      }

      setLoadingPermissions(true)
      try {
        const crudAPI = api("crud")
        const response = await crudAPI({
          get: {
            model: "Permission",
            match: { user: user._id }
          }
        })

        if (Array.isArray(response)) {
          setPermissions(response.length > 0 && response[0].permissions ? response[0].permissions : [])
        } else if (response.data && Array.isArray(response.data)) {
          setPermissions(response.data.length > 0 && response.data[0].permissions ? response.data[0].permissions : [])
        } else {
          setPermissions([])
        }
      } catch (error) {
        console.error("Failed to load permissions:", error)
        setPermissions([])
      } finally {
        setLoadingPermissions(false)
      }
    }

    loadPermissions()
  }, [isOpen, user])

  // Load store data for the user
  useEffect(() => {
    const loadStoreData = async () => {
      if (!isOpen || !user || !user._id || activeTab !== 'store') {
        return
      }

      setLoadingStore(true)
      try {
        const crudAPI = api("crud")

        // Load transaction count
        const transactionsResponse = await crudAPI({
          get: {
            model: "StoreTransaction",
            match: { user: user._id }
          }
        })

        // Load StoreAccount for LTV
        const accountResponse = await crudAPI({
          get: {
            model: "StoreAccount",
            match: { user: user._id },
            select: "ltv"
          }
        })

        // Load active subscription
        const subscriptionResponse = await crudAPI({
          get: {
            model: "ProductSubscription",
            match: { user: user._id, status: "active" },
            select: "pid",
            populate: ["product"]
          }
        })

        const transactionCount = Array.isArray(transactionsResponse) ? transactionsResponse.length : 0
        const ltv = accountResponse && accountResponse[0] && accountResponse[0].ltv ? accountResponse[0].ltv : []
        const activeSubscription = subscriptionResponse && subscriptionResponse[0] ? subscriptionResponse[0] : null

        setStoreData({
          transactionCount,
          ltv,
          subscription: activeSubscription
        })
      } catch (error) {
        console.error("Failed to load store data:", error)
        setStoreData({
          transactionCount: 0,
          ltv: [],
          subscription: null
        })
      } finally {
        setLoadingStore(false)
      }
    }

    loadStoreData()
  }, [isOpen, user, activeTab])

  const renderField = (label, value) => (
    <div className="flex justify-between py-3 border-b border-admin_bordered last:border-b-0">
      <Typography variant="admin_txt2_dim" className="my-0">
        {label}
      </Typography>
      <Typography variant="admin_body" className="my-0 font-medium text-right break-all ml-4">
        {value || "N/A"}
      </Typography>
    </div>
  )

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <Typography variant="admin_txt1_dim">Loading user information...</Typography>
        </div>
      )
    }

    if (error || !user) {
      return (
        <div className="flex items-center justify-center p-12">
          <Typography variant="admin_txt1_dim" className="text-admin_error">
            {error || "User not found"}
          </Typography>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-6 pb-8">
        {/* User Header Section */}
        <div className="flex gap-6">
          {/* User Avatar */}
          <div className="flex-shrink-0">
            <Tooltip content="Click to change avatar">
              <div
                className="w-32 h-32 rounded-full bg-admin_primary flex items-center justify-center text-white text-4xl font-bold border-4 border-admin_bordered relative group cursor-pointer"
                onClick={() => setIsMediaLibraryOpen(true)}
              >
                {user.avatar ? (
                  <img
                    src={url(user.avatar)}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                    data-user-avatar={user._id}
                  />
                ) : (
                  <span>{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
                )}
                {/* Camera icon overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {updatingAvatar ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  ) : (
                    <CameraIcon className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>
            </Tooltip>
          </div>

          {/* User Info */}
          <div className="flex-1">
            {/* User Name */}
            <div className="flex items-center gap-2 mb-2">
              {editingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    variant="admin"
                    className="flex-1"
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        try {
                          const crudAPI = api("crud")
                          const response = await crudAPI({
                            update: {
                              model: "User",
                              match: { _id: user._id },
                              data: { name: editedName }
                            }
                          })

                          if (response && response.result !== false) {
                            setUser({ ...user, name: editedName })

                            // Update the name in the DataTable immediately
                            const nameElements = document.querySelectorAll(`[data-user-name="${user._id}"]`)
                            nameElements.forEach(element => {
                              element.textContent = editedName
                            })

                            showNotification({
                              message: "Name updated successfully",
                              className: "bg-admin_success text-white"
                            })
                            setEditingName(false)
                          } else {
                            showNotification({
                              message: response.error || "Failed to update name",
                              className: "bg-admin_error text-white"
                            })
                          }
                        } catch (error) {
                          console.error("Failed to update name:", error)
                          showNotification({
                            message: "Failed to update name",
                            className: "bg-admin_error text-white"
                          })
                        }
                      } else if (e.key === 'Escape') {
                        setEditingName(false)
                      }
                    }}
                  />
                  <Button
                    variant="admin_secondary"
                    onClick={() => setEditingName(false)}
                    className="px-3 py-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="admin_primary"
                    onClick={async () => {
                      try {
                        const crudAPI = api("crud")
                        const response = await crudAPI({
                          update: {
                            model: "User",
                            match: { _id: user._id },
                            data: { name: editedName }
                          }
                        })

                        if (response && response.result !== false) {
                          setUser({ ...user, name: editedName })

                          // Update the name in the DataTable immediately
                          const nameElements = document.querySelectorAll(`[data-user-name="${user._id}"]`)
                          nameElements.forEach(element => {
                            element.textContent = editedName
                          })

                          showNotification({
                            message: "Name updated successfully",
                            className: "bg-admin_success text-white"
                          })
                          setEditingName(false)
                        } else {
                          showNotification({
                            message: response.error || "Failed to update name",
                            className: "bg-admin_error text-white"
                          })
                        }
                      } catch (error) {
                        console.error("Failed to update name:", error)
                        showNotification({
                          message: "Failed to update name",
                          className: "bg-admin_error text-white"
                        })
                      }
                    }}
                    className="px-3 py-1"
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <>
                  <Typography variant="admin_h2" className="my-0">
                    {user.name || "Unknown User"}
                  </Typography>
                  <Tooltip content="Edit name">
                    <button
                      onClick={() => {
                        setEditedName(user.name || "")
                        setEditingName(true)
                      }}
                      className="text-admin_text_secondary hover:text-admin_primary transition-colors p-1"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                  </Tooltip>
                </>
              )}
            </div>

            {/* Email */}
            <Typography variant="admin_body" className="text-admin_text_secondary mb-4">
              {user.email || "No email"}
            </Typography>

            {/* Status Badges */}
            <div className="flex items-center gap-2 mb-4">
              {user.enabled !== undefined ? (
                user.enabled ? (
                  <Badge variant="admin_success">Active</Badge>
                ) : (
                  <Badge variant="admin_error">Disabled</Badge>
                )
              ) : null}
              {permissions && permissions.includes("role:admin") && (
                <Badge variant="admin_warning">Admin</Badge>
              )}
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {user.devices && Array.isArray(user.devices) && user.devices.length > 0 && (
                <div className="bg-admin_canvas rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <DevicePhoneMobileIcon className="w-4 h-4 text-admin_text_secondary" />
                      <Typography variant="admin_txt2_dim" className="my-0">
                        Devices
                      </Typography>
                    </div>
                    <Typography variant="admin_h4" className="my-0 text-admin_primary">
                      {user.devices.length}
                      {user.deviceLimit && user.deviceLimit > 0 && ` / ${user.deviceLimit}`}
                    </Typography>
                  </div>
                </div>
              )}
              {apiKeys && apiKeys.length > 0 && (
                <div className="bg-admin_canvas rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <KeyIcon className="w-4 h-4 text-admin_text_secondary" />
                      <Typography variant="admin_txt2_dim" className="my-0">
                        API Keys
                      </Typography>
                    </div>
                    <Typography variant="admin_h4" className="my-0 text-admin_primary">
                      {apiKeys.filter(k => k.status === 'active').length}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-admin_bordered"></div>

        {/* Tabbed Details Section */}
        <Tabs
          variant="admin"
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            {
              value: "details",
              label: "Details"
            },
            {
              value: "credentials",
              label: "Credentials"
            },
            {
              value: "permissions",
              label: "Permissions & Access"
            },
            {
              value: "store",
              label: "Store"
            },
            ...(user.devices && user.devices.length > 0 ? [{
              value: "devices",
              label: "Devices"
            }] : []),
            ...(apiKeys && apiKeys.length > 0 ? [{
              value: "api_keys",
              label: "API Keys"
            }] : []),
            ...(user.metadata && Object.keys(user.metadata).length > 0 ? [{
              value: "metadata",
              label: "Metadata"
            }] : [])
          ]}
        />

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === "details" && (
            <div className="divide-y divide-admin_bordered">
              {renderField("User ID", user._id)}
              {renderField("Name", user.name)}
              {renderField("Email", user.email)}
              {user.phone && renderField("Phone", user.phone)}
              {user.country && renderField("Country", user.country)}
              {user.language && renderField("Language", user.language)}
              {renderField("Created", user.created ? moment(user.created).format("MMM DD, YYYY HH:mm") : "N/A")}
              {renderField("Last Updated", user.updated ? moment(user.updated).format("MMM DD, YYYY HH:mm") : "N/A")}
              {renderField("Enabled", user.enabled ? "Yes" : "No")}
            </div>
          )}

          {activeTab === "credentials" && (
            <div className="divide-y divide-admin_bordered">
              {user.email && renderField("Email", user.email)}
              {user.google_id && renderField("Google ID", user.google_id)}
              {user.apple_id && renderField("Apple ID", user.apple_id)}
              {user.facebook_id && renderField("Facebook ID", user.facebook_id)}
              {user.stripe_customer_id && renderField("Stripe Customer ID", user.stripe_customer_id)}
              {user.internal_id && renderField("Internal ID", user.internal_id)}
            </div>
          )}

          {activeTab === "permissions" && (
            <div className="space-y-4">
              {/* Permissions List */}
              <div>
                <Typography variant="admin_label" className="mb-2 flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5" />
                  Permissions
                </Typography>
                {loadingPermissions ? (
                  <Typography variant="admin_txt2_dim">Loading permissions...</Typography>
                ) : permissions && permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {permissions.map((permission, idx) => (
                      <Badge
                        key={idx}
                        variant={permission.includes("admin") ? "admin_warning" : "admin_info"}
                      >
                        {permission}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Typography variant="admin_txt2_dim">No permissions assigned</Typography>
                )}
              </div>

              {/* Device Limit */}
              {user.deviceLimit !== undefined && (
                <div className="pt-3 border-t border-admin_bordered">
                  {renderField("Device Limit", user.deviceLimit || "Unlimited")}
                  {user.devices && renderField("Devices Connected", Array.isArray(user.devices) ? user.devices.length : 0)}
                </div>
              )}
            </div>
          )}

          {activeTab === "devices" && user.devices && (
            <DevicesTab user={user} />
          )}

          {activeTab === "api_keys" && (
            <div className="space-y-3">
              <Typography variant="admin_label" className="mb-2">
                API Keys ({apiKeys.filter(k => k.status === 'active').length} active)
              </Typography>
              {loadingApiKeys ? (
                <div className="flex items-center justify-center p-8">
                  <Typography variant="admin_txt1_dim">Loading API keys...</Typography>
                </div>
              ) : apiKeys.length > 0 ? (
                <div className="space-y-3">
                  {apiKeys.map((apiKey, idx) => (
                    <div key={idx} className="bg-admin_canvas rounded-lg p-4 border border-admin_bordered">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <KeyIcon className="w-5 h-5 text-admin_text_secondary flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            {apiKey.description && (
                              <Typography variant="admin_body" className="my-0 font-medium">
                                {apiKey.description}
                              </Typography>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={apiKey.status === 'active' ? "admin_success" : "admin_error"}>
                                {apiKey.status === 'active' ? "Active" : apiKey.status === 'revoked' ? "Revoked" : "Disabled"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="admin_secondary"
                          onClick={() => {
                            setSelectedApiKey(apiKey)
                            setShowInspectModal(true)
                          }}
                          className="flex-shrink-0 ml-2"
                        >
                          Inspect
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography variant="admin_txt2_dim">No API keys</Typography>
              )}
            </div>
          )}

          {activeTab === "metadata" && user.metadata && Object.keys(user.metadata).length > 0 && (
            <div className="divide-y divide-admin_bordered">
              {Object.entries(user.metadata).map(([key, value]) => (
                <div key={key} className="py-3">
                  <Typography variant="admin_txt2_dim" className="mb-1">{key}</Typography>
                  <div className="ml-4">
                    {typeof value === 'object' ? (
                      <pre className="whitespace-pre-wrap font-mono text-xs bg-admin_neutral_1 p-3 rounded text-admin_text">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      <Typography variant="admin_body" className="break-all my-0">
                        {String(value)}
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "store" && (
            <div className="space-y-4">
              {loadingStore ? (
                <div className="flex items-center justify-center p-8">
                  <Typography variant="admin_txt1_dim">Loading store data...</Typography>
                </div>
              ) : storeData ? (
                <div className="divide-y divide-admin_bordered">
                  {/* LTV per currency */}
                  {storeData.ltv && storeData.ltv.length > 0 && (
                    <div className="py-3 border-b border-admin_bordered">
                      <div className="flex justify-between">
                        <Typography variant="admin_txt2_dim" className="my-0">LTV</Typography>
                        <div className="text-right space-y-1">
                          {storeData.ltv.map((entry, idx) => {
                            const currencySymbols = {
                              USD: '$',
                              EUR: '€',
                              GBP: '£'
                            }
                            const symbol = currencySymbols[entry.currency] || entry.currency
                            return (
                              <Typography key={idx} variant="admin_body" className="my-0 font-medium">
                                {symbol}{entry.amount.toFixed(2)}
                              </Typography>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* LTV Unified (if multiple currencies) */}
                  {storeData.ltv && storeData.ltv.length > 1 &&
                    renderField(
                      "LTV Unified",
                      `$${(() => {
                        // Calculate unified LTV using exchange rates
                        const exchangeRates = {
                          EUR: 1.09,
                          GBP: 1.27,
                          CAD: 0.74,
                          AUD: 0.67,
                          JPY: 0.0071,
                          CHF: 1.15,
                          CNY: 0.14,
                          INR: 0.012,
                          BRL: 0.20,
                          MXN: 0.058
                        }

                        let totalUSD = 0
                        storeData.ltv.forEach(entry => {
                          if (entry.currency === 'USD') {
                            totalUSD += entry.amount
                          } else if (exchangeRates[entry.currency]) {
                            totalUSD += entry.amount * exchangeRates[entry.currency]
                          }
                        })

                        return totalUSD.toFixed(2)
                      })()}`
                    )
                  }

                  {renderField("Transactions", storeData.transactionCount)}

                  {renderField(
                    "Subscription",
                    storeData.subscription
                      ? (storeData.subscription.product?.name || storeData.subscription.pid || "Active")
                      : "No"
                  )}
                </div>
              ) : (
                <Typography variant="admin_txt2_dim">No store data available</Typography>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Handler for status change
  const handleStatusChange = async (newStatus) => {
    if (!user) return

    try {
      const enabled = newStatus === 'true'
      const response = await UserPlatformAPI.ToggleUserStatus(user._id, enabled)

      if (response.result) {
        setUser({ ...user, enabled })
        showNotification({
          message: `User ${enabled ? 'enabled' : 'disabled'} successfully`,
          className: "bg-admin_success text-white"
        })
        onStatusChange?.(user, enabled)
      } else {
        showNotification({
          message: response.error || "Failed to update user status",
          className: "bg-admin_error text-white"
        })
      }
    } catch (error) {
      console.error("Failed to update user status:", error)
      showNotification({
        message: "Failed to update user status",
        className: "bg-admin_error text-white"
      })
    }
  }

  // Handler for avatar change
  const handleAvatarSelect = async (file) => {
    if (!user || !file) return

    const filePath = file.path || file.url
    if (!filePath) return

    setUpdatingAvatar(true)
    try {
      const crudAPI = api("crud")
      const response = await crudAPI({
        update: {
          model: "User",
          match: { _id: user._id },
          data: { avatar: filePath }
        }
      })

      if (response && response.result !== false) {
        setUser({ ...user, avatar: filePath })

        // Update all avatar images on the page for this user
        updateUserAvatarOnPage(user._id, filePath)

        showNotification({
          message: "Avatar updated successfully",
          className: "bg-admin_success text-white"
        })
      } else {
        showNotification({
          message: response.error || "Failed to update avatar",
          className: "bg-admin_error text-white"
        })
      }
    } catch (error) {
      console.error("Failed to update avatar:", error)
      showNotification({
        message: "Failed to update avatar",
        className: "bg-admin_error text-white"
      })
    } finally {
      setUpdatingAvatar(false)
      setIsMediaLibraryOpen(false)
    }
  }

  // Handler for delete user
  const handleDeleteUser = async () => {
    if (!user) return

    try {
      const response = await UserPlatformAPI.DeleteUsers([user._id])

      if (response.result) {
        showNotification({
          message: "User deleted successfully",
          className: "bg-admin_success text-white"
        })
        onDelete?.(user)
        onClose()
      } else {
        showNotification({
          message: response.error || "Failed to delete user",
          className: "bg-admin_error text-white"
        })
      }
    } catch (error) {
      console.error("Failed to delete user:", error)
      showNotification({
        message: "Failed to delete user",
        className: "bg-admin_error text-white"
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between w-full">
          <Typography variant="admin_h3" className="my-0 ml-12">
            User Information
          </Typography>
          {user && (
            <div className="flex items-center gap-3">
              {/* Status Dropdown */}
              <DropdownSelect
                variant="admin"
                options={[
                  { value: 'true', name: 'Active' },
                  { value: 'false', name: 'Disabled' }
                ]}
                value={user.enabled
                  ? [{ value: 'true', name: 'Active' }]
                  : [{ value: 'false', name: 'Disabled' }]
                }
                onChange={(values) => {
                  const selectedValue = values?.[0]?.value || 'false'
                  handleStatusChange(selectedValue)
                }}
                showCheckbox={false}
                fixedWidth="120px"
              />

              {/* Delete Button */}
              <Button
                variant="admin_error"
                onClick={handleDeleteUser}
                className="px-3 py-1"
              >
                <TrashIcon className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      }
      variant="admin_drawer_right"
      closeOnClickOutside={false}
      passInteractionEvents={true}
      styleOverrides={{
        header: "relative flex justify-between items-center px-6 py-6 border-b border-admin_bordered",
        closeButton: "absolute left-2 top-1/2 -translate-y-1/2 hover:text-admin_text z-10",
        container: "w-full h-full max-w-4xl flex flex-col bg-admin_paper"
      }}
    >
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
        <div className="p-6 pb-12">
          {renderContent()}
        </div>
      </div>

      {/* Inspect API Key Modal */}
      {showInspectModal && selectedApiKey && (
        <Modal
          isOpen={showInspectModal}
          onClose={() => {
            setShowInspectModal(false)
            setSelectedApiKey(null)
          }}
          title="Inspect API Key"
          variant="admin"
          className="max-w-2xl"
        >
          <div className="p-6">
            <APIKeyForm
              mode="edit"
              apiKey={selectedApiKey}
              onSuccess={() => {
                setShowInspectModal(false)
                setSelectedApiKey(null)
                // Reload API keys
                const reloadApiKeys = async () => {
                  if (!user || !user._id) return
                  setLoadingApiKeys(true)
                  try {
                    const crudAPI = api("crud")
                    const response = await crudAPI({
                      get: {
                        model: "APIKey",
                        match: { user: user._id },
                        select: "key description status validUntil lastUsedAt createdAt",
                        sort: { createdAt: -1 }
                      }
                    })
                    if (Array.isArray(response)) {
                      setApiKeys(response)
                    } else if (response.data && Array.isArray(response.data)) {
                      setApiKeys(response.data)
                    } else {
                      setApiKeys([])
                    }
                  } catch (error) {
                    console.error("Failed to reload API keys:", error)
                  } finally {
                    setLoadingApiKeys(false)
                  }
                }
                reloadApiKeys()
              }}
              onCancel={() => {
                setShowInspectModal(false)
                setSelectedApiKey(null)
              }}
              onDelete={() => {
                setShowInspectModal(false)
                setSelectedApiKey(null)
                // Reload API keys
                const reloadApiKeys = async () => {
                  if (!user || !user._id) return
                  setLoadingApiKeys(true)
                  try {
                    const crudAPI = api("crud")
                    const response = await crudAPI({
                      get: {
                        model: "APIKey",
                        match: { user: user._id },
                        select: "key description status validUntil lastUsedAt createdAt",
                        sort: { createdAt: -1 }
                      }
                    })
                    if (Array.isArray(response)) {
                      setApiKeys(response)
                    } else if (response.data && Array.isArray(response.data)) {
                      setApiKeys(response.data)
                    } else {
                      setApiKeys([])
                    }
                  } catch (error) {
                    console.error("Failed to reload API keys:", error)
                  } finally {
                    setLoadingApiKeys(false)
                  }
                }
                reloadApiKeys()
              }}
            />
          </div>
        </Modal>
      )}

      {/* Media Library for Avatar Selection */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onFileSelect={handleAvatarSelect}
        title="Select Avatar"
        defaultFileType="image"
        allowMultiSelect={false}
      />
    </Modal>
  )
}
